import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getCookie, setCookie, deleteCookie } from "@/utils/cookies";
import { useGetClientQuery } from "@/queries/useGetClientQuery";
import { queryClient } from "@/lib/queryClient";
import { clearYandexMetrikaUserParams, updateYandexMetrikaUserParams } from "@/utils/metrika";

interface AuthContextType {
  isAuth: boolean;
  userName: string;
  userPhone: string;
  authPhone: string; // Phone used for authentication (single, normalized)
  setUserName: (name: string) => void;
  login: (token: string, name: string, phone: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuth, setIsAuth] = useState(() => getCookie("isAuth") === "true");
  const [userName, setUserNameState] = useState(() => localStorage.getItem("client_name") || "");

  const setUserName = (name: string) => {
    setUserNameState(name);
    if (name) {
      localStorage.setItem("client_name", name);
    }
  };
  const [userPhone, setUserPhone] = useState("");
  const [authPhone, setAuthPhone] = useState(() => getCookie("phone") || "");
  const { data: clientData } = useGetClientQuery({ enabled: isAuth });

  useEffect(() => {
    if (isAuth && clientData) {
      updateYandexMetrikaUserParams(clientData);
      setUserPhone(clientData.phone);
    }
  }, [isAuth, clientData]);

  const login = (token: string, name: string, phone: string) => {
    setCookie("user_token", token);
    setCookie("isAuth", "true", 365);
    setCookie("phone", phone);
    setIsAuth(true);
    setUserName(name);
    setAuthPhone(phone);
  };

  const logout = () => {
    deleteCookie("user_token");
    deleteCookie("phone");
    deleteCookie("isAuth");

    // Clear localStorage items
    localStorage.removeItem("pass_token");
    localStorage.removeItem("phone");
    localStorage.removeItem("client_name");

    // Clear React Query cache to prevent data leakage between users
    queryClient.clear();

    // Clear analytics state when user leaves the session
    clearYandexMetrikaUserParams();

    setIsAuth(false);
    setUserName("");
    setUserPhone("");
    setAuthPhone("");
  };

  return (
    <AuthContext.Provider
      value={{ isAuth, userName, userPhone, authPhone, setUserName, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
